// @ts-nocheck
/**
 * @fileoverview SectionCard — titled card wrapper for entity page sections.
 *
 * Pure layout component. Renders a card with heading, optional subtitle,
 * and a divider before the children. Has ZERO knowledge of attributes,
 * fields, or form state.
 *
 * Used by Entity pages for both "Preliminary Details" and "Descriptive Information"
 * sections. Can be reused by any page that needs a titled card.
 *
 * @example
 * <SectionCard title="Preliminary Details" subtitle="Core attributes for this entity.">
 *   <Grid container spacing={3}>{...}</Grid>
 * </SectionCard>
 */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import SolyticsCard from '../../../../../package/components/SolyticsCard.jsx';
import SolyticsBox from '../../../../../package/components/SolyticsBox';
import SolyticsTypography from '../../../../../package/components/SolyticsTypography';
import SolyticsDivider from '../../../../../package/components/SolyticsDivider';

const SectionCard = ({
  title,
  subtitle = '',
  children,
  mode = 'edit',
  maxHeight = '100%',
  minHeight = '0',
  headerActions = null,
  headerAlignTop = false,
}) => {
  const isEditCreateMode = useMemo(
    () => mode === 'edit' || mode === 'create',
    [mode]
  );

  const showSubtitle = useMemo(
    () => isEditCreateMode && subtitle,
    [isEditCreateMode, subtitle]
  );

  const header = (title || subtitle || headerActions) && (
    <SolyticsBox
      sx={{
        px: 1.75,
        py: 1.25,
        minHeight: '56px',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: headerAlignTop ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <SolyticsBox>
        <SolyticsTypography variant="subtitle2">{title}</SolyticsTypography>
        {showSubtitle && (
          <SolyticsTypography variant="caption" color="text.secondary">
            {subtitle}
          </SolyticsTypography>
        )}
      </SolyticsBox>
      {headerActions && <SolyticsBox>{headerActions}</SolyticsBox>}
    </SolyticsBox>
  );

  return (
    <SolyticsCard
      variant="outlined"
      width="100%"
      maxHeight={maxHeight}
      minHeight={minHeight}
      disableContentPadding
      disableCardHeaderWrapper
      cardHeader={header}
      scrollableContent={true}
    >
      <SolyticsDivider />
      <SolyticsBox
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </SolyticsBox>
    </SolyticsCard>
  );
};

SectionCard.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  mode: PropTypes.oneOf(['edit', 'create', 'view']),
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  headerActions: PropTypes.node,
  headerAlignTop: PropTypes.bool,
};

export default React.memo(SectionCard);
